// src/components/Footer.js
import React from 'react';
import { useInView } from 'react-intersection-observer';

const footerLinks = {
  products: {
    title: 'Products',
    links: [
      { name: 'Data Center Proxies', href: '#pricing' },
      { name: 'Residential Proxies', href: '#pricing' },
      { name: 'Elite Proxies', href: '#pricing' },

    ]
  },
  company: {
    title: 'Company',
    links: [
      { name: 'Features', href: '#features' },
      { name: 'Pricing', href: '#pricing' },
      { name: 'Contact', href: '#contact' },
      {name:'Status', href:"https://nxp.statuspage.io/"}
    ]
  }
};

const socialLinks = [
  {
    name: 'WhatsApp',
    href: 'https://wa.me/254700919007',
    icon: (
      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
      </svg>
    ),
  },
];

// src/components/Footer.js
// ... (imports and socialLinks remain the same)

const Footer = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };

  return (
    <footer className="relative py-16 md:py-24 overflow-hidden" ref={ref}>
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-accent/50 to-transparent" />
      </div>

      <div className="container mx-auto px-4">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12 md:mb-16">
          {/* Brand Column */}
          <div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center space-x-2 mb-6">
              <span className="text-2xl font-bold text-accent">NexaProxy</span>
              <span className="px-2 py-1 text-xs bg-accent rounded-full text-dark-bg">V3</span>
            </div>
            <p className="text-text-secondary max-w-sm mb-6">
              Advanced Privacy & Quality Premium Proxies for Anonymity.
              Secure, Fast, and Reliable proxy solutions for all your needs.
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="text-text-secondary hover:text-accent transition-colors"
                >
                  {item.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Products Column */}
          <div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative z-10"
          >
            <h3 className="text-lg font-semibold mb-6">{footerLinks.products.title}</h3>
            <ul className="space-y-4">
              {footerLinks.products.links.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToSection('pricing')}
                    className="text-text-secondary hover:text-accent transition-colors cursor-pointer"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative z-10"
          >
            <h3 className="text-lg font-semibold mb-6">{footerLinks.company.title}</h3>
            <ul className="space-y-4">
              {footerLinks.company.links.map((link) => (
                <li key={link.name}>
<button
  onClick={() => {
    if (link.href.startsWith('#')) {
      // Internal section scroll
      scrollToSection(link.href.slice(1));
    } else {
      // External redirect (opens in new tab)
      window.open(link.href, '_blank');
    }
  }}
  className="text-text-secondary hover:text-accent transition-colors cursor-pointer"
>
  {link.name}
</button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="pt-8 border-t border-gray-800"
        >
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-text-secondary text-sm">
              © {new Date().getFullYear()} Techanics Inc. All rights reserved.
            </div>
            <div className="flex flex-wrap justify-center gap-6 md:gap-12">
              <button 
                className="text-text-secondary hover:text-accent text-sm transition-colors cursor-pointer"
                onClick={() => scrollToSection('privacy')}
              >
                Privacy Policy
              </button>
              <button 
                className="text-text-secondary hover:text-accent text-sm transition-colors cursor-pointer"
                onClick={() => scrollToSection('terms')}
              >
                Terms of Service
              </button>
              <button 
                className="text-text-secondary hover:text-accent text-sm transition-colors cursor-pointer"
                onClick={() => scrollToSection('cookies')}
              >
                Cookie Policy
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;